import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'For IP Owners', href: '/ip-owners' },
    { name: 'For Property Partners', href: '/property-partners' },
    { name: 'For Investors', href: '/investors' },
    { name: 'Platform', href: '/product' },
    { name: 'Roadmap', href: '/roadmap' },
    { name: 'Team', href: '/team' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl" aria-label="Main navigation">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center hover:opacity-80 transition-opacity"
            data-testid="logo-link"
          >
            <img 
              src="/images/nexus-lbe-logo-black.png" 
              alt="NEXUS LBE" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:gap-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-['Inter'] font-medium transition-colors hover:text-primary ${
                  location.pathname === item.href
                    ? 'text-primary font-semibold'
                    : 'text-muted-foreground'
                }`}
                data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button
                className="h-10 px-5 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-white"
                data-testid="header-contact-button"
              >
                Get in Touch
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <nav className="flex flex-col gap-4 mt-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`text-base font-['Inter'] font-medium py-2 transition-colors hover:text-primary ${
                      location.pathname === item.href
                        ? 'text-primary font-semibold'
                        : 'text-foreground'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="mt-4 pt-4 border-t border-border">
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full bg-primary hover:bg-[hsl(17_62%_34%)] text-primary-foreground">
                      Get in Touch
                    </Button>
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Header;
